/**
 * Drag-and-drop upload zone for skipper track uploads.
 * Validates files, triggers skipper upload flow, shows progress.
 */

import { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { UploadState } from '@/lib/upload';
import { validateFile, isValidExtension } from '@/lib/upload';
import { uploadSkipperTrack } from '@/lib/skipper-upload';
import { UploadProgressBar } from './UploadProgressBar';

export interface UploadDropzoneProps {
  regattaId: string;
  onUploadComplete?: (uploadId: string) => void;
  onError?: (fileName: string, error: string) => void;
}

const ACCEPT = '.vkx,.vtk,.gpx,.csv,.fit,.usr';

export function UploadDropzone({
  regattaId,
  onUploadComplete,
  onError,
}: UploadDropzoneProps) {
  const [uploads, setUploads] = useState<Map<string, UploadState>>(new Map());
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const updateUpload = useCallback((key: string, state: UploadState) => {
    setUploads((prev) => {
      const next = new Map(prev);
      next.set(key, state);
      return next;
    });
  }, []);

  const processFile = useCallback(
    async (file: File) => {
      const key = `${file.name}-${Date.now()}`;

      const validationError = validateFile(file);
      if (validationError) {
        updateUpload(key, {
          file,
          phase: 'error',
          progress: 0,
          error: validationError,
        });
        onError?.(file.name, validationError);
        return;
      }

      try {
        const result = await uploadSkipperTrack(file, {
          regattaId,
          onStateChange: (state) => updateUpload(key, state),
        });
        onUploadComplete?.(result.uploadId);
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Upload failed';
        updateUpload(key, {
          file,
          phase: 'error',
          progress: 0,
          error: errorMsg,
        });
        onError?.(file.name, errorMsg);
      }
    },
    [regattaId, onUploadComplete, onError, updateUpload]
  );

  const handleFiles = useCallback(
    (files: FileList | File[]) => {
      for (const file of Array.from(files)) {
        processFile(file);
      }
    },
    [processFile]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files).filter((f) =>
        isValidExtension(f.name)
      );

      if (files.length === 0 && e.dataTransfer.files.length > 0) {
        const names = Array.from(e.dataTransfer.files)
          .map((f) => f.name)
          .join(', ');
        onError?.(names, 'No valid track files found. Accepted: VKX, VTK, GPX, CSV, FIT, USR');
        return;
      }

      handleFiles(files);
    },
    [handleFiles, onError]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        handleFiles(e.target.files);
        e.target.value = '';
      }
    },
    [handleFiles]
  );

  const retryUpload = useCallback(
    (key: string) => {
      const existing = uploads.get(key);
      if (existing) {
        processFile(existing.file);
      }
    },
    [uploads, processFile]
  );

  const uploadEntries = Array.from(uploads.entries());

  return (
    <div className="space-y-3" data-testid="upload-dropzone">
      {/* Drop zone */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Upload track files"
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          flex cursor-pointer flex-col items-center justify-center gap-2
          rounded-lg border-2 border-dashed p-8 transition-colors
          ${
            isDragOver
              ? 'border-brand-blue bg-blue-50'
              : 'border-gray-300 bg-gray-50 hover:border-brand-blue hover:bg-blue-50/50'
          }
        `}
        data-testid="dropzone-area"
      >
        <svg
          className={`h-10 w-10 ${isDragOver ? 'text-brand-ink' : 'text-gray-400'}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
          />
        </svg>
        <div className="text-center">
          <p className="text-sm font-medium text-navy-900">
            {isDragOver ? 'Drop files here' : 'Drag & drop your track files here'}
          </p>
          <p className="text-xs text-muted-foreground">
            or click to browse — VKX, VTK, GPX, CSV, FIT
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            By uploading, you grant RegattaReplay™ the right to use this data per our{' '}
            <Link
              to="/terms"
              target="_blank"
              className="underline hover:text-navy-700"
              onClick={(e) => e.stopPropagation()}
            >
              Terms of Service
            </Link>
            .
          </p>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        multiple
        className="hidden"
        onChange={handleInputChange}
        data-testid="file-input"
      />

      {/* Upload progress list */}
      {uploadEntries.length > 0 && (
        <div className="space-y-2">
          {uploadEntries.map(([key, state]) => (
            <UploadProgressBar
              key={key}
              fileName={state.file.name}
              fileSize={state.file.size}
              phase={state.phase}
              progress={state.progress}
              error={state.error}
              onRetry={state.phase === 'error' ? () => retryUpload(key) : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}
